import {useEffect, useState} from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import {useTheme} from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import {LocalizationProvider, DatePicker} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {format} from "date-fns";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {setLoggedInUserDetail, useRayzayUIController} from "../../context";
import {getAllUserByUserName, updateCurrentUser} from "../../api/user/userApiClient";
import {updateAdmin} from "../../api/admin/adminApiClient";
import {updateBranchAdmin} from "../../api/branchadmin/branchAdminApiClient";
import {showSuccess, showError} from "../../utils/notificationUtils";
import axiosApiClient from "../../api/axios";
import {formatMalaysianPhone, validateMalaysianPhone} from "../../utils/formValidations";

function Overview() {
    const theme = useTheme();

    const [controller, dispatch] = useRayzayUIController();
    const {loggedInUserDetail} = controller;

    const [profileForm, setProfileForm] = useState({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        gender: "",
        birthDate: null,
    });
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordErrors, setPasswordErrors] = useState({});
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        if (loggedInUserDetail) {
            setProfileForm({
                firstName: loggedInUserDetail.firstName || "",
                lastName: loggedInUserDetail.lastName || "",
                phoneNumber: loggedInUserDetail.phoneNumber || "",
                gender: loggedInUserDetail.gender || "",
                birthDate: loggedInUserDetail.birthDate ? new Date(loggedInUserDetail.birthDate) : null,
            });
        }
    }, [loggedInUserDetail]);

    const handleProfileChange = (field, value) => {
        setProfileForm((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (formErrors[field]) {
            setFormErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    const resetProfileForm = () => {
        if (!loggedInUserDetail) return;
        setProfileForm({
            firstName: loggedInUserDetail.firstName || "",
            lastName: loggedInUserDetail.lastName || "",
            phoneNumber: loggedInUserDetail.phoneNumber || "",
            gender: loggedInUserDetail.gender || "",
            birthDate: loggedInUserDetail.birthDate ? new Date(loggedInUserDetail.birthDate) : null,
        });
        setFormErrors({});
    };

    const handleSaveProfile = async () => {
        if (!loggedInUserDetail) return;

        const errors = {};
        if (!profileForm.firstName) {
            errors.firstName = "First name is required";
        }
        if (!profileForm.lastName) {
            errors.lastName = "Last name is required";
        }
        const phoneError = validateMalaysianPhone(profileForm.phoneNumber);
        if (phoneError) {
            errors.phoneNumber = phoneError;
        }
        if (!profileForm.gender) {
            errors.gender = "Gender is required";
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setSaving(true);
        setFormErrors((prev) => ({...prev, submit: ""}));

        try {
            const submitData = {
                firstName: profileForm.firstName || "",
                lastName: profileForm.lastName || "",
                email: loggedInUserDetail.email || "",
                phoneNumber: profileForm.phoneNumber,
                gender: profileForm.gender,
                userRole: loggedInUserDetail.userRole,
                idType: loggedInUserDetail.idType || "",
                idNumber: loggedInUserDetail.idNumber || "",
                birthDate: profileForm.birthDate ? format(profileForm.birthDate, "yyyy-MM-dd") : null,
                status: loggedInUserDetail.status || "ACTIVE",
                username: loggedInUserDetail.username || "",
            };

            // Call appropriate API based on role
            if (loggedInUserDetail.userRole === "BRANCH_ADMIN") {
                await updateBranchAdmin(loggedInUserDetail.id, submitData);
            } else if (
                loggedInUserDetail.userRole === "ADMIN" ||
                loggedInUserDetail.userRole === "SYSTEM_ADMIN"
            ) {
                await updateAdmin(loggedInUserDetail.id, submitData);
            } else {
                // For regular users (e.g., GUARDIAN, STAFF), update their own profile
                await updateCurrentUser(submitData);
            }

            const refreshed = await getAllUserByUserName(loggedInUserDetail.username);
            setLoggedInUserDetail(dispatch, refreshed.data);
            setFormErrors({});

            showSuccess("Profile updated successfully");
        } catch (error) {
            console.error("Failed to update profile", error);
            const message =
                error?.response?.data?.message ||
                error.message ||
                "Failed to update profile";
            setFormErrors((prev) => ({
                ...prev,
                submit: message,
            }));
            showError(message);
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (!loggedInUserDetail) return;

        const errors = {};
        if (!currentPassword) {
            errors.currentPassword = "Current password is required";
        }
        if (!newPassword) {
            errors.newPassword = "New password is required";
        } else if (newPassword.length < 8) {
            errors.newPassword = "New password must be at least 8 characters long";
        }
        if (!confirmPassword) {
            errors.confirmPassword = "Please confirm new password";
        } else if (newPassword && confirmPassword !== newPassword) {
            errors.confirmPassword = "New password and confirmation do not match";
        }

        if (Object.keys(errors).length > 0) {
            setPasswordErrors(errors);
            return;
        }

        setChangingPassword(true);
        setPasswordErrors(prev => ({...prev, submit: ""}));

        try {
            const response = await axiosApiClient.post("/api/auth/change-password", {
                username: loggedInUserDetail.username,
                currentPassword,
                newPassword,
                confirmPassword,
            });

            if (response.status === 200) {
                showSuccess("Password changed successfully");
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setPasswordErrors({});
            }
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err.message ||
                "Failed to change password";
            setPasswordErrors(prev => ({
                ...prev,
                submit: message,
            }));
            showError(message);
        } finally {
            setChangingPassword(false);
        }
    };

    if (!loggedInUserDetail) {
        return (
            <DashboardLayout>
                <SoftBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="50vh"
                >
                    <SoftTypography variant="h6" color="text.secondary">
                        Loading user profile...
                    </SoftTypography>
                </SoftBox>
            </DashboardLayout>
        );
    }

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Not provided';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Helper function to format datetime
    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'Not provided';
        return new Date(dateTimeString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Helper function to get role display name
    const getRoleDisplayName = (role) => {
        const roleMap = {
            'SYSTEM_ADMIN': 'System Administrator',
            'ADMIN': 'Administrator',
            'BRANCH_ADMIN': 'Branch Administrator',
            'STAFF': 'Staff Member',
            'GUARDIAN': 'Guardian'
        };
        return roleMap[role] || role;
    };

    // Helper function to get status color
    const getStatusColor = (status) => {
        const statusMap = {
            'ACTIVE': 'success',
            'INACTIVE': 'error',
            'IDLE': 'warning'
        };
        return statusMap[status] || 'default';
    };

    return (
        <DashboardLayout>
            <SoftBox sx={{
                background: `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[300]} 100%)`,
                minHeight: '100vh',
                p: {xs: 1, sm: 2}
            }}>
                <SoftBox mt={3} mb={3}>
                    <Grid container spacing={3}>
                        {/* Profile Info Card */}
                        <Grid item xs={12}>
                            <Card sx={{
                                boxShadow: 4,
                                borderRadius: 3,
                                background: 'rgba(255,255,255,0.95)',
                                backdropFilter: 'blur(10px)',
                                overflow: 'hidden',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 6
                                }
                            }}>
                                <SoftBox sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.focus} 100%)`,
                                    p: {xs: 2, sm: 3},
                                    color: 'white'
                                }}>
                                    <SoftBox display="flex" alignItems="center">
                                        <SoftBox>
                                            <SoftTypography variant="h4" fontWeight="bold" color="white">
                                                Profile Information
                                            </SoftTypography>
                                            <SoftTypography variant="body1" color="rgba(255,255,255,0.8)">
                                                Hi,
                                                I&apos;m {loggedInUserDetail.firstName || 'User'} {loggedInUserDetail.lastName || ''}
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>
                                </SoftBox>

                                <SoftBox p={3}>
                                    <Grid container spacing={3}>
                                        {/* Profile Picture and Basic Info */}
                                        <Grid item xs={12} md={4}>
                                            <SoftBox display="flex" justifyContent="center" alignItems="center"
                                                     flexDirection="column">
                                                <SoftBox sx={{
                                                    position: 'relative',
                                                    mb: 2
                                                }}>
                                                    {loggedInUserDetail.profilePic ? (
                                                        <img
                                                            src={`data:image/png;base64,${loggedInUserDetail.profilePic}`}
                                                            alt="profile"
                                                            style={{
                                                                width: 150,
                                                                height: 150,
                                                                borderRadius: '50%',
                                                                objectFit: 'cover',
                                                                border: '4px solid white',
                                                                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                                                            }}
                                                        />
                                                    ) : (
                                                        <SoftBox
                                                            sx={{
                                                                width: 150,
                                                                height: 150,
                                                                borderRadius: '50%',
                                                                backgroundColor: 'grey.300',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                border: '4px solid white',
                                                                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                                                            }}
                                                        >
                                                            <Icon sx={{fontSize: 60, color: 'grey.500'}}>
                                                                person
                                                            </Icon>
                                                        </SoftBox>
                                                    )}
                                                    <SoftBox sx={{
                                                        position: 'absolute',
                                                        bottom: 8,
                                                        right: 8,
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'success.main',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: '3px solid white',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                                    }}>
                                                        <Icon sx={{color: 'white', fontSize: 20}}>check</Icon>
                                                    </SoftBox>
                                                </SoftBox>
                                                <SoftTypography variant="h6" fontWeight="bold" color="primary" mb={1}>
                                                    {loggedInUserDetail.firstName || 'Unknown'} {loggedInUserDetail.lastName || ''}
                                                </SoftTypography>
                                                <SoftTypography variant="body2" color="text.secondary" mb={1}>
                                                    {getRoleDisplayName(loggedInUserDetail.userRole || 'USER')}
                                                </SoftTypography>
                                                <Chip
                                                    label={loggedInUserDetail.status || 'Unknown'}
                                                    color={getStatusColor(loggedInUserDetail.status || 'UNKNOWN')}
                                                    size="small"
                                                    sx={{fontWeight: 'bold', mb: 2}}
                                                />

                                                {/* Additional Info Cards under Profile Picture */}
                                                <SoftBox sx={{width: '100%', mt: 2}}>
                                                    <SoftBox sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(103, 126, 234, 0.05)',
                                                        border: '1px solid rgba(103, 126, 234, 0.1)',
                                                        mb: 1
                                                    }}>
                                                        <Icon color="primary" sx={{fontSize: 20}}>email</Icon>
                                                        <SoftBox>
                                                            <SoftTypography variant="body2" color="text.secondary"
                                                                            fontWeight="medium" fontSize="0.75rem">
                                                                Email / Username
                                                            </SoftTypography>
                                                            <SoftTypography variant="body2" fontWeight="bold"
                                                                            fontSize="0.8rem">
                                                                {loggedInUserDetail.email}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(76, 175, 80, 0.05)',
                                                        border: '1px solid rgba(76, 175, 80, 0.1)',
                                                        mb: 1
                                                    }}>
                                                        <Icon color="success" sx={{fontSize: 20}}>badge</Icon>
                                                        <SoftBox>
                                                            <SoftTypography variant="body2" color="text.secondary"
                                                                            fontWeight="medium" fontSize="0.75rem">
                                                                ID Type
                                                            </SoftTypography>
                                                            <SoftTypography variant="body2" fontWeight="bold"
                                                                            fontSize="0.8rem">
                                                                {loggedInUserDetail.idType || 'Not provided'}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(76, 175, 80, 0.05)',
                                                        border: '1px solid rgba(76, 175, 80, 0.1)',
                                                        mb: 1
                                                    }}>
                                                        <Icon color="success" sx={{fontSize: 20}}>person</Icon>
                                                        <SoftBox>
                                                            <SoftTypography variant="body2" color="text.secondary"
                                                                            fontWeight="medium" fontSize="0.75rem">
                                                                ID Number
                                                            </SoftTypography>
                                                            <SoftTypography variant="body2" fontWeight="bold"
                                                                            fontSize="0.8rem">
                                                                {loggedInUserDetail.idNumber || 'Not provided'}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(76, 175, 80, 0.05)',
                                                        border: '1px solid rgba(76, 175, 80, 0.1)',
                                                        mb: 1
                                                    }}>
                                                        <Icon color="success" sx={{fontSize: 20}}>calendar_today</Icon>
                                                        <SoftBox>
                                                            <SoftTypography variant="body2" color="text.secondary"
                                                                            fontWeight="medium" fontSize="0.75rem">
                                                                Member Since
                                                            </SoftTypography>
                                                            <SoftTypography variant="body2" fontWeight="bold"
                                                                            fontSize="0.8rem">
                                                                {formatDate(loggedInUserDetail.createdAt)}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(255, 152, 0, 0.05)',
                                                        border: '1px solid rgba(255, 152, 0, 0.1)',
                                                        mb: 1
                                                    }}>
                                                        <Icon color="warning" sx={{fontSize: 20}}>schedule</Icon>
                                                        <SoftBox>
                                                            <SoftTypography variant="body2" color="text.secondary"
                                                                            fontWeight="medium" fontSize="0.75rem">
                                                                Last Login
                                                            </SoftTypography>
                                                            <SoftTypography variant="body2" fontWeight="bold"
                                                                            fontSize="0.8rem">
                                                                {formatDateTime(loggedInUserDetail.lastLogin)}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>

                                                    {loggedInUserDetail.updatedAt && (
                                                        <SoftBox sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 2,
                                                            borderRadius: 2,
                                                            backgroundColor: 'rgba(0, 188, 212, 0.05)',
                                                            border: '1px solid rgba(0, 188, 212, 0.1)',
                                                            mb: 1
                                                        }}>
                                                            <Icon color="info" sx={{fontSize: 20}}>update</Icon>
                                                            <SoftBox>
                                                                <SoftTypography variant="body2" color="text.secondary"
                                                                                fontWeight="medium" fontSize="0.75rem">
                                                                    Last Updated
                                                                </SoftTypography>
                                                                <SoftTypography variant="body2" fontWeight="bold"
                                                                                fontSize="0.8rem">
                                                                    {formatDateTime(loggedInUserDetail.updatedAt)}
                                                                </SoftTypography>
                                                            </SoftBox>
                                                        </SoftBox>
                                                    )}
                                                </SoftBox>
                                            </SoftBox>
                                        </Grid>

                                        {/* Profile Details */}
                                        <Grid item xs={12} md={8}>
                                            <SoftBox>

                                                {/* Personal Details (editable) */}
                                                <SoftTypography variant="h6" fontWeight="bold" color="primary" mb={2}>
                                                    Personal Details
                                                </SoftTypography>
                                                <Grid container spacing={2} mb={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <SoftBox mb={0.5} ml={0.5}>
                                                            <SoftTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                            >
                                                                First Name
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <TextField
                                                            fullWidth
                                                            placeholder="Enter first name"
                                                            value={profileForm.firstName}
                                                            onChange={(e) =>
                                                                handleProfileChange("firstName", e.target.value)
                                                            }
                                                            error={!!formErrors.firstName}
                                                            helperText={formErrors.firstName}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <SoftBox mb={0.5} ml={0.5}>
                                                            <SoftTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                            >
                                                                Last Name
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <TextField
                                                            fullWidth
                                                            placeholder="Enter last name"
                                                            value={profileForm.lastName}
                                                            onChange={(e) =>
                                                                handleProfileChange("lastName", e.target.value)
                                                            }
                                                            error={!!formErrors.lastName}
                                                            helperText={formErrors.lastName}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <SoftBox mb={0.5} ml={0.5}>
                                                            <SoftTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                            >
                                                                Phone Number
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <TextField
                                                            fullWidth
                                                            placeholder="Enter phone number in E.164 format (e.g., +60133034946)"
                                                            value={profileForm.phoneNumber}
                                                            onChange={(e) => {
                                                                const formatted = formatMalaysianPhone(e.target.value);
                                                                handleProfileChange("phoneNumber", formatted);
                                                            }}
                                                            error={!!formErrors.phoneNumber}
                                                            helperText={formErrors.phoneNumber}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <SoftBox mb={0.5} ml={0.5}>
                                                            <SoftTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                            >
                                                                Gender
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <TextField
                                                            select
                                                            fullWidth
                                                            value={profileForm.gender || ""}
                                                            onChange={(e) =>
                                                                handleProfileChange("gender", e.target.value)
                                                            }
                                                            error={!!formErrors.gender}
                                                            helperText={formErrors.gender}
                                                        >
                                                            <MenuItem value="">
                                                                <em>Select gender</em>
                                                            </MenuItem>
                                                            <MenuItem value="MALE">Male</MenuItem>
                                                            <MenuItem value="FEMALE">Female</MenuItem>
                                                            <MenuItem value="OTHER">Other</MenuItem>
                                                        </TextField>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <SoftBox mb={0.5} ml={0.5}>
                                                            <SoftTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                            >
                                                                Birth Date
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                            <DatePicker
                                                                value={profileForm.birthDate}
                                                                onChange={(date) =>
                                                                    handleProfileChange("birthDate", date)
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        fullWidth
                                                                        placeholder="Select birth date"
                                                                        error={
                                                                            !!formErrors.birthDate || params.error
                                                                        }
                                                                        helperText={
                                                                            formErrors.birthDate || params.helperText
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                        </LocalizationProvider>
                                                    </Grid>
                                                </Grid>

                                                <SoftBox
                                                    display="flex"
                                                    justifyContent="flex-end"
                                                    gap={1}
                                                    mb={2}
                                                >
                                                    <SoftButton
                                                        variant="text"
                                                        color="text"
                                                        onClick={resetProfileForm}
                                                        disabled={saving}
                                                    >
                                                        Reset
                                                    </SoftButton>
                                                    <SoftButton
                                                        variant="gradient"
                                                        color="primary"
                                                        onClick={handleSaveProfile}
                                                        disabled={saving}
                                                    >
                                                        {saving ? "Saving..." : "Save Changes"}
                                                    </SoftButton>
                                                </SoftBox>
                                                <Divider sx={{my: 3}}/>

                                                {/* Change Password */}
                                                <SoftTypography variant="h6" fontWeight="bold" color="primary" mb={2}>
                                                    Change Password
                                                </SoftTypography>
                                                <Grid container spacing={2} mb={2}>
                                                    <Grid item xs={12}>
                                                        <SoftBox mb={0.5} display="flex" justifyContent="center">
                                                            <SoftTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                            >
                                                                Existing Password
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox display="flex" justifyContent="center">
                                                            <TextField
                                                                sx={{ width: "50%" }}
                                                                type="password"
                                                                placeholder="Enter current password"
                                                                value={currentPassword}
                                                                onChange={(e) => {
                                                                    setCurrentPassword(e.target.value);
                                                                    if (passwordErrors.currentPassword) {
                                                                        setPasswordErrors(prev => ({
                                                                            ...prev,
                                                                            currentPassword: ""
                                                                        }));
                                                                    }
                                                                }}
                                                                error={!!passwordErrors.currentPassword}
                                                                helperText={passwordErrors.currentPassword}
                                                            />
                                                        </SoftBox>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <SoftBox mb={0.5} display="flex" justifyContent="center">
                                                            <SoftTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                            >
                                                                New Password
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox display="flex" justifyContent="center">
                                                            <TextField
                                                                sx={{ width: "50%" }}
                                                                type="password"
                                                                placeholder="Enter new password"
                                                                value={newPassword}
                                                                onChange={(e) => {
                                                                    setNewPassword(e.target.value);
                                                                    if (passwordErrors.newPassword) {
                                                                        setPasswordErrors(prev => ({
                                                                            ...prev,
                                                                            newPassword: ""
                                                                        }));
                                                                    }
                                                                }}
                                                                error={!!passwordErrors.newPassword}
                                                                helperText={passwordErrors.newPassword}
                                                            />
                                                        </SoftBox>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <SoftBox mb={0.5} display="flex" justifyContent="center">
                                                            <SoftTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                            >
                                                                Confirm New Password
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox display="flex" justifyContent="center">
                                                            <TextField
                                                                sx={{ width: "50%" }}
                                                                type="password"
                                                                placeholder="Re-enter new password"
                                                                value={confirmPassword}
                                                                onChange={(e) => {
                                                                    setConfirmPassword(e.target.value);
                                                                    if (passwordErrors.confirmPassword) {
                                                                        setPasswordErrors(prev => ({
                                                                            ...prev,
                                                                            confirmPassword: ""
                                                                        }));
                                                                    }
                                                                }}
                                                                error={!!passwordErrors.confirmPassword}
                                                                helperText={passwordErrors.confirmPassword}
                                                            />
                                                        </SoftBox>
                                                    </Grid>
                                                </Grid>

                                                <SoftBox
                                                    display="flex"
                                                    justifyContent="flex-end"
                                                    gap={1}
                                                    mb={2}
                                                >
                                                    <SoftButton
                                                        variant="text"
                                                        color="text"
                                                        onClick={() => {
                                                            setCurrentPassword("");
                                                            setNewPassword("");
                                                            setConfirmPassword("");
                                                            setPasswordErrors({});
                                                        }}
                                                        disabled={changingPassword}
                                                    >
                                                        Reset
                                                    </SoftButton>
                                                    <SoftButton
                                                        variant="gradient"
                                                        color="primary"
                                                        onClick={handleChangePassword}
                                                        disabled={changingPassword}
                                                    >
                                                        {changingPassword ? "Changing..." : "Save Password"}
                                                    </SoftButton>
                                                </SoftBox>
                                            </SoftBox>
                                        </Grid>
                                    </Grid>
                                </SoftBox>
                            </Card>
                        </Grid>
                    </Grid>
                </SoftBox>
            </SoftBox>
        </DashboardLayout>
    );
}

export default Overview;
